import { useState, useEffect, useContext } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LanguageContext } from "../App";
import { useLocation } from "react-router-dom";
import HotelLogo from "./HotelLogo";

const navLinks = [
  { key: "rooms",      path: "/camere" },
  { key: "experience", path: "/esperienza" },
  { key: "location",   path: "/posizione" },
  { key: "gallery",    path: "/galleria" },
  { key: "contacts",   path: "/contatti" },
];

export default function Navbar() {
  const { lang, setLang, t } = useContext(LanguageContext);
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const isHome = pathname === "/";

  useEffect(() => {
    if (!isHome) { setScrolled(true); return; }
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isHome]);

  // Close menu on route change
  useEffect(() => { setMenuOpen(false); }, [pathname]);

  const textColor = scrolled ? "text-[#1A1A19]" : "text-white";
  const subColor  = scrolled ? "text-[#6B6A68]" : "text-white/80";
  const langMuted = scrolled ? "text-[#6B6A68]" : "text-white/80";

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/90 backdrop-blur-xl shadow-sm"
          : "bg-gradient-to-b from-black/40 to-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-3 md:px-8 flex items-center justify-between h-16 md:h-20">

        {/* Logo SVG — autocontenuto, nessuna dipendenza esterna */}
        <Link to="/" data-testid="logo-btn" className="flex-shrink-0">
          <HotelLogo
            className={`h-14 md:h-[68px] w-auto transition-all duration-300 ${
              scrolled ? "text-[#2A2726]" : "text-white"
            }`}
            style={!scrolled ? {
              filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.8)) drop-shadow(0 0 2px rgba(0,0,0,0.6))"
            } : {}}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <NavLink
              key={link.key}
              to={link.path}
              data-testid={`nav-${link.key}`}
              className={({ isActive }) =>
                `font-['Manrope',sans-serif] text-xs uppercase tracking-[0.2em] transition-colors duration-300 hover:opacity-70 ${textColor} ${
                  isActive ? "opacity-100 decoration-[#7B907F] underline underline-offset-4" : "opacity-80"
                }`
              }
            >
              {t.nav[link.key]}
            </NavLink>
          ))}
        </nav>

        {/* Right: lang switcher + book CTA */}
        <div className="flex items-center gap-4">
          {/* Language switcher */}
          <div
            data-testid="lang-switcher"
            className={`hidden sm:flex items-center gap-1 font-['Manrope',sans-serif] text-xs tracking-widest ${langMuted}`}
          >
            {["it", "en", "de", "nl"].map((l, i) => (
              <span key={l} className="flex items-center gap-1">
                <button
                  data-testid={`lang-${l}`}
                  onClick={() => { setLang(l); setMenuOpen(false); }}
                  className={`uppercase transition-all duration-200 hover:opacity-100 ${
                    lang === l
                      ? `${textColor} font-medium`
                      : "opacity-40"
                  }`}
                >
                  {l}
                </button>
                {i < 3 && <span className="opacity-20">|</span>}
              </span>
            ))}
          </div>

          {/* Book CTA */}
          <Link
            to="/prenota"
            data-testid="nav-book-btn"
            className={`hidden md:inline-flex items-center font-['Manrope',sans-serif] text-xs uppercase tracking-[0.2em] px-6 py-2.5 transition-all duration-300 ${
              scrolled
                ? "bg-[#7B907F] text-white hover:bg-[#687A6C]"
                : "bg-white text-[#2A2726] hover:bg-white/90"
            }`}
          >
            {t.nav.bookNow}
          </Link>

          {/* Mobile toggle */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMenuOpen(!menuOpen)}
            className={`md:hidden transition-colors duration-300 ${textColor}`}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-white/95 backdrop-blur-xl border-t border-[#E2DFD8] px-6 py-6 flex flex-col gap-5"
        >
          {navLinks.map((link) => (
            <Link
              key={link.key}
              to={link.path}
              data-testid={`mobile-nav-${link.key}`}
              className="font-['Manrope',sans-serif] text-sm uppercase tracking-[0.2em] text-[#1A1A19] text-left hover:opacity-60 transition-opacity"
            >
              {t.nav[link.key]}
            </Link>
          ))}
          {/* Lang */}
          <div className="flex items-center gap-3 pt-2 border-t border-[#E2DFD8]">
            {["it", "en", "de", "nl"].map((l) => (
              <button
                key={l}
                data-testid={`mobile-lang-${l}`}
                onClick={() => { setLang(l); setMenuOpen(false); }}
                className={`font-['Manrope',sans-serif] text-xs uppercase tracking-widest transition-all ${
                  lang === l ? "text-[#1A1A19] font-semibold" : "text-[#9A9996]"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
          <Link
            to="/prenota"
            data-testid="mobile-book-btn"
            className="bg-[#7B907F] text-white font-['Manrope',sans-serif] text-xs uppercase tracking-[0.2em] px-6 py-3 text-center hover:bg-[#687A6C] transition-colors"
          >
            {t.nav.bookNow}
          </Link>
        </div>
      )}
    </header>
  );
}
